import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "guardianForm",
    "bookingForMyself",
    "bookingAsGuardian",
    "purchaserForm",
    "firstName",
    "lastName",
    "cellNumber",
    "cellNumberLabel",
    "genderMale",
    "genderFemale",
    "genderNonBinary",
    "studentEmail",
    "studentEmailLabel",
    "guardianFirstName",
    "guardianLastName",
    "guardianCellNumber",
    "guardianCellNumberLabel",
    "guardianEmail",
    "guardianEmailLabel",
    "purchaserFirstName",
    "purchaserLastName",
    "purchaserCellNumber",
    "purchaserEmail",
    "adultWarningBlock",
    "adultWarningText",
  ];

  connect() {
    setTimeout(() => {
      this.relationShipToStudentChanged();
    });
  }

  // Use inline style alongside hidden so display:flex from Grid class doesn't win
  showTarget(el) {
    el.hidden = false;
    el.style.display = "";
  }

  hideTarget(el) {
    el.hidden = true;
    el.style.display = "none";
  }

  permitRequiredForCurrentStudent() {
    const age = this.calculateAge();
    const permitRequiredTeen = this.element.dataset.permitRequiredTeen === "true";
    const permitRequiredAdult = this.element.dataset.permitRequiredAdult === "true";

    return age >= 18 ? permitRequiredAdult : permitRequiredTeen;
  }

  validPermitDatesRequiredForCurrentStudent() {
    return this.element.dataset.validPermitDatesRequired === "true";
  }

  validateForm() {
    const studentEmailRequired = this.isStudentEmailRequired();
    const guardianEmailRequired = !studentEmailRequired;

    let errors = [];
    if (
      this.firstNameTarget.value == undefined ||
      this.firstNameTarget.value == ""
    ) {
      errors.push("First name is required");
    }
    if (
      this.lastNameTarget.value == undefined ||
      this.lastNameTarget.value == ""
    ) {
      errors.push("Last name is required");
    }
    if (
      studentEmailRequired &&
      (this.cellNumberTarget.value == undefined ||
        this.cellNumberTarget.value == "")
    ) {
      errors.push("Cell number is required");
    }
    if (
      studentEmailRequired &&
      (this.studentEmailTarget.value == undefined ||
        this.studentEmailTarget.value == "")
    ) {
      errors.push("Email is required");
    } else if (this.studentEmailTarget.value) {
      // check if email is valid via a regex
      const email = this.studentEmailTarget.value;
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(email)) {
        errors.push("Email is not in a valid format");
      }
    }

    if (
      this.hasGenderNonBinaryTarget &&
      this.genderMaleTarget.checked === false &&
      this.genderFemaleTarget.checked === false &&
      this.genderNonBinaryTarget.checked === false
    ) {
      errors.push("Gender is required");
    } else if (
      !this.hasGenderNonBinaryTarget &&
      this.genderMaleTarget.checked === false &&
      this.genderFemaleTarget.checked === false
    ) {
      errors.push("Gender is required");
    }

    // if guardianForm is visible, validate guardian form
    if (this.guardianFormTarget.hidden == false) {
      if (
        this.guardianFirstNameTarget.value == undefined ||
        this.guardianFirstNameTarget.value == ""
      ) {
        errors.push("Guardian first name is required");
      }
      if (
        this.guardianLastNameTarget.value == undefined ||
        this.guardianLastNameTarget.value == ""
      ) {
        errors.push("Guardian last name is required");
      }
      if (
        this.guardianCellNumberTarget.value == undefined ||
        this.guardianCellNumberTarget.value == ""
      ) {
        errors.push("Guardian cell number is required");
      }
      if (
        this.guardianEmailTarget.value == undefined ||
        this.guardianEmailTarget.value == ""
      ) {
        errors.push("Guardian email is required");
      } else {
        // check if email is valid via a regex
        const email = this.guardianEmailTarget.value;
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(email)) {
          errors.push("Guardian email is not in a valid format");
        }
        // Check guardian email is not the same as student email
        if (this.studentEmailTarget.value == this.guardianEmailTarget.value) {
          errors.push("Student and parent/guardian email cannot be the same");
        }
      }
    }

    let dateOfBirth = this.getSelectedDate(
      "driving_school_booking_date_of_birth"
    );

    if (!this.isValidDate(dateOfBirth)) {
      errors.push("Please enter a valid date of birth.");
    }

    const permitRequired = this.permitRequiredForCurrentStudent();
    const permitDatesRequired =
      permitRequired &&
      this.validPermitDatesRequiredForCurrentStudent();
    const skippingPermit = document.getElementById("permitName")?.checked;

    if (permitDatesRequired && !skippingPermit) {
      let issueDate = this.getSelectedDate(
        "driving_school_booking_permit_issue_date"
      );

      if (!this.isValidDate(issueDate)) {
        errors.push("Please enter a valid permit issue date.");
      }

      let expiryDate = this.getSelectedDate(
        "driving_school_booking_permit_expiration"
      );

      if (!this.isValidDate(expiryDate)) {
        errors.push("Please enter a valid permit expiry date.");
      }
    }

    // if purchaserForm is visible, validate purchaser form
    if (this.purchaserFormTarget.hidden == false) {
      //purchaser_first_name
      if (
        this.purchaserFirstNameTarget.value == undefined ||
        this.purchaserFirstNameTarget.value == ""
      ) {
        errors.push("Purchaser first name is required");
      }
      if (
        this.purchaserLastNameTarget.value == undefined ||
        this.purchaserLastNameTarget.value == ""
      ) {
        errors.push("Purchaser last name is required");
      }
      if (
        this.purchaserCellNumberTarget.value == undefined ||
        this.purchaserCellNumberTarget.value == ""
      ) {
        errors.push("Purchaser cell number is required");
      }
      if (
        this.purchaserEmailTarget.value == undefined ||
        this.purchaserEmailTarget.value == ""
      ) {
        errors.push("Purchaser email is required");
      }
    }

    return errors;
  }

  getSelectedDate(prefix) {
    var year = document.getElementById(prefix + "_1i").value;
    var month = document.getElementById(prefix + "_2i").value;
    var day = document.getElementById(prefix + "_3i").value;
    return year + "-" + month + "-" + day; // returns a string in "YYYY-MM-DD" format
  }

  isValidDate(dateString) {
    const parts = dateString.split("-");
    if (parts.length !== 3 || parts.some((part) => part === "")) {
      return false;
    }

    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in JavaScript Date
    const day = parseInt(parts[2], 10);

    const date = new Date(year, month, day);

    // Check if the date has rolled over to the next month or year, which means the day was invalid
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month ||
      date.getDate() !== day
    ) {
      return false; // The date rolled over, so it's invalid
    }

    return true;
  }

  completedForm(event) {
    event.preventDefault();

    const errors = this.validateForm();
    const validationBlock = document.getElementById(
      "validation-personal-block"
    );
    const validationMessage = document.getElementById(
      "validation-message-personal-text"
    );

    if (errors.length > 0) {
      validationBlock.hidden = false;
      validationMessage.innerHTML = errors.join("<br>");
      validationMessage.hidden = false;
      return;
    }

    this.element.dispatchEvent(
      new CustomEvent("personalDetailsComplete", {
        detail: {
          formName: "driving-school-admin-booking-form-v2-personal-details",
          lessonDetails: {},
        },
      })
    );
  }

  bookingForSelf(event) {
    this.relationShipToStudentChanged();
  }

  bookingForOther(event) {
    this.relationShipToStudentChanged();
  }

  relationShipToStudentChanged(event) {
    this.changeWhichFieldsAreRequired();
    this.updateAdultWarning();
    const age = this.calculateAge();
    const bookingForMyself = this.bookingForMyselfTarget.checked == true;
    const bookingAsGuardian = this.bookingAsGuardianTarget.checked == true;

    if (bookingForMyself == false && bookingAsGuardian == false) {
      this.showTarget(this.purchaserFormTarget);
    } else {
      this.hideTarget(this.purchaserFormTarget);
    }

    // If under 18, show guardian form
    if (age < 18) {
      this.showTarget(this.guardianFormTarget);
      return;
    }

    // If booking as guardian, show guardian form
    if (bookingAsGuardian == true) {
      this.showTarget(this.guardianFormTarget);
      return;
    }

    // If over 18 and booking for themselves, hide guardian form
    this.hideTarget(this.guardianFormTarget);
  }

  updateAdultWarning() {
    if (!this.hasAdultWarningBlockTarget) {
      return;
    }

    const skipScheduleInput = document.getElementById("is_not_scheduling_lessons");
    const scheduling = skipScheduleInput ? skipScheduleInput.value === "false" : false;
    const hasDateOfBirth = this.getDateofBirth() != null;
    const isAdult = this.calculateAge() >= 18;

    if (scheduling && hasDateOfBirth && isAdult) {
      const name = `${this.firstNameTarget.value} ${this.lastNameTarget.value}`.trim();
      this.adultWarningTextTarget.textContent = `${name.length > 0 ? name : "This student"} is an adult`;
      this.adultWarningBlockTarget.hidden = false;
    } else {
      this.adultWarningBlockTarget.hidden = true;
    }
  }

  changeWhichFieldsAreRequired() {
    const isStudentEmailRequired = this.isStudentEmailRequired();
    const guardianEmailRequired = !isStudentEmailRequired;

    const studentEmailInput = this.studentEmailTarget;
    const studentEmailLabel = this.studentEmailLabelTarget;
    const studentCellNumberInput = this.cellNumberTarget;
    const studentCellNumberLabel = this.cellNumberLabelTarget;

    if (isStudentEmailRequired) {
      studentEmailInput.required = true;
      studentEmailInput.classList.add("isRequiredLocalised");
      studentEmailLabel.classList.add("isRequiredLocalised");
      studentEmailInput.classList.remove("isRecommendedLocalised");
      studentEmailLabel.classList.remove("isRecommendedLocalised");
      studentCellNumberInput.required = true;
      studentCellNumberInput.classList.add("isRequiredLocalised");
      studentCellNumberLabel.classList.add("isRequiredLocalised");
      studentCellNumberInput.classList.remove("isRecommendedLocalised");
      studentCellNumberLabel.classList.remove("isRecommendedLocalised");
    } else {
      studentEmailInput.required = false;
      studentEmailInput.classList.remove("isRequiredLocalised");
      studentEmailLabel.classList.remove("isRequiredLocalised");
      studentEmailInput.classList.add("isRecommendedLocalised");
      studentEmailLabel.classList.add("isRecommendedLocalised");
      studentCellNumberInput.required = false;
      studentCellNumberInput.classList.remove("isRequiredLocalised");
      studentCellNumberLabel.classList.remove("isRequiredLocalised");
      studentCellNumberInput.classList.add("isRecommendedLocalised");
      studentCellNumberLabel.classList.add("isRecommendedLocalised");
    }

    const guardianEmailInput = this.guardianEmailTarget;
    const guardianEmailLabel = this.guardianEmailLabelTarget;
    const guardianCellNumberInput = this.guardianCellNumberTarget;
    const guardianCellNumberLabel = this.guardianCellNumberLabelTarget;

    if (guardianEmailRequired) {
      guardianEmailInput.required = true;
      guardianEmailInput.classList.add("isRequiredLocalised");
      guardianEmailLabel.classList.add("isRequiredLocalised");
      guardianEmailInput.classList.remove("isRecommendedLocalised");
      guardianEmailLabel.classList.remove("isRecommendedLocalised");
      guardianCellNumberInput.required = true;
      guardianCellNumberInput.classList.add("isRequiredLocalised");
      guardianCellNumberLabel.classList.add("isRequiredLocalised");
      guardianCellNumberInput.classList.remove("isRecommendedLocalised");
      guardianCellNumberLabel.classList.remove("isRecommendedLocalised");
    } else {
      guardianEmailInput.required = false;
      guardianEmailInput.classList.remove("isRequiredLocalised");
      guardianEmailLabel.classList.remove("isRequiredLocalised");
      guardianEmailInput.classList.add("isRecommendedLocalised");
      guardianEmailLabel.classList.add("isRecommendedLocalised");
      guardianCellNumberInput.required = false;
      guardianCellNumberInput.classList.remove("isRequiredLocalised");
      guardianCellNumberLabel.classList.remove("isRequiredLocalised");
      guardianCellNumberInput.classList.add("isRecommendedLocalised");
      guardianCellNumberLabel.classList.add("isRecommendedLocalised");
    }
  }

  updatePermitFieldsRequiredFromRule() {
    const permitInput = document.getElementById("permit_number_value");
    const permitLabel = document.getElementById("permit_number_label");
    const issueLabel = document.getElementById("permit_issue_date_label");
    const expiryLabel = document.getElementById("permit_expiration_label");
    if (!permitInput || !permitLabel) {
      return;
    }

    const permitRequired = this.permitRequiredForCurrentStudent();
    const validDatesRequired =
      permitRequired && this.validPermitDatesRequiredForCurrentStudent();

    this.setPermitFieldRequiredState(permitInput, permitLabel, permitRequired);
    if (issueLabel) {
      this.setPermitFieldRequiredState(null, issueLabel, validDatesRequired);
    }
    if (expiryLabel) {
      this.setPermitFieldRequiredState(null, expiryLabel, validDatesRequired);
    }

    const datesCheckboxRegion = document.getElementById(
      "permit-dates-checkbox-region"
    );
    if (datesCheckboxRegion) {
      datesCheckboxRegion.classList.toggle(
        "PermitDatesCheckboxRegion--optional",
        !permitRequired
      );
      datesCheckboxRegion.classList.toggle(
        "PermitDatesCheckboxRegion--required",
        permitRequired
      );
    }

    const permitNoPermitCheckbox = document.getElementById("permitName");
    const permitDateFields = document.getElementById("permit-date-fields");
    const permitNumberFields = document.getElementById("permit-number-fields");
    const bookingDetailsNoPermit = document.getElementById(
      "booking-details-no-permit"
    );

    if (permitRequired && permitNoPermitCheckbox) {
      permitNoPermitCheckbox.checked = false;
      if (permitDateFields) {
        permitDateFields.hidden = false;
      }
      if (permitNumberFields) {
        permitNumberFields.hidden = false;
      }
      if (bookingDetailsNoPermit) {
        bookingDetailsNoPermit.hidden = true;
      }
      this.setPermitFieldsDisabled(false);
    } else if (!permitRequired) {
      if (bookingDetailsNoPermit) {
        bookingDetailsNoPermit.hidden = true;
      }
      if (permitNoPermitCheckbox && permitDateFields && permitNumberFields) {
        const checked = permitNoPermitCheckbox.checked;
        permitDateFields.hidden = checked;
        permitNumberFields.hidden = checked;
        this.setPermitFieldsDisabled(checked);
      }
    }
  }

  setPermitFieldRequiredState(input, label, isRequired) {
    if (label) {
      label.classList.toggle("isRequired", isRequired);
      label.classList.toggle("isRecommended", !isRequired);
    }
    if (input) {
      input.required = isRequired;
      input.classList.toggle("isRequired", isRequired);
      input.classList.toggle("isRecommended", !isRequired);
    }
  }

  setPermitFieldsDisabled(disabled) {
    const numberEl = document.getElementById("permit-number-fields");
    const dateEl = document.getElementById("permit-date-fields");
    [numberEl, dateEl].forEach((el) => {
      if (!el) return;
      el.querySelectorAll("input, select").forEach((field) => {
        field.disabled = disabled;
      });
    });
  }

  toggleNoPermit(event) {
    const checked = event.currentTarget.checked;
    const permitRequired = this.permitRequiredForCurrentStudent();
    const dateFields = document.getElementById("permit-date-fields");
    const numberFields = document.getElementById("permit-number-fields");
    const advicePanel = document.getElementById("booking-details-no-permit");

    if (!permitRequired) {
      if (dateFields) {
        dateFields.hidden = checked;
      }
      if (numberFields) {
        numberFields.hidden = checked;
      }
      if (advicePanel) {
        advicePanel.hidden = true;
      }
      this.setPermitFieldsDisabled(checked);
      return;
    }

    if (dateFields) {
      dateFields.hidden = false;
    }
    if (numberFields) {
      numberFields.hidden = false;
    }
    if (advicePanel) {
      advicePanel.hidden = !checked;
    }
    this.setPermitFieldsDisabled(false);
  }

  isStudentEmailRequired() {
    // We previously had this logic:
    const age = this.calculateAge();
    const isAdult = age >= 18;
    if (this.bookingAsGuardianTarget.checked == false) {
      return isAdult;
    }
    return this.bookingAsGuardianTarget.checked != true;
    // Changed because even if adult, but the spouse is booking, we don't need student email
  }

  calculateAge() {
    var dateOfBirth = this.getDateofBirth();
    if (!dateOfBirth) {
      return 0;
    }

    const parts = dateOfBirth.split("-");
    const formattedDateOfBirth = parts.join("/");
    var dob = new Date(formattedDateOfBirth);
    var today = new Date();

    var age = today.getFullYear() - dob.getFullYear();
    var monthDiff = today.getMonth() - dob.getMonth();
    var dayDiff = today.getDate() - dob.getDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }

    return age;
  }

  getDateofBirth() {
    const yearElement = document.getElementById(
      "driving_school_booking_date_of_birth_1i"
    );
    const monthElement = document.getElementById(
      "driving_school_booking_date_of_birth_2i"
    );
    const dayElement = document.getElementById(
      "driving_school_booking_date_of_birth_3i"
    );

    if (!yearElement || !monthElement || !dayElement) {
      return null;
    }

    const day = dayElement.value;
    const month = monthElement.value;
    const year = yearElement.value;

    if (!year || !month || !day) {
      return null;
    }

    return `${year}-${month}-${day}`;
  }
}
